import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getMostActiveClubs,
  getTrendingClubs,
  getNewClubs,
  getTopClubs,
  globalClubActivityFeed,
  activityTypeConfig,
  clubScoreActions,
  clubTypeLabels,
  clubTypeIcons,
  clubTypeColors,
  clubsSummary,
  type RfwClub,
  type ClubType,
  type ClubActivityItem,
} from '@/mocks/rfwClubs';

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/* ─── Activity Item ─── */
function ActivityItem({ item }: { item: ClubActivityItem }) {
  const cfg = activityTypeConfig[item.type];
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-background-50 hover:bg-background-100/70 transition-colors duration-200">
      <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center shrink-0`}>
        <i className={`${cfg.icon} ${cfg.color} text-xs`}></i>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-xs font-semibold text-foreground-950">{item.user.name}</span>
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
        </div>
        <p className="text-[11px] text-foreground-600 leading-relaxed">{item.content}</p>
        <div className="flex items-center gap-2 mt-1">
          {item.targetTitle && (
            <span className="text-[9px] text-foreground-400">en {item.targetTitle}</span>
          )}
          <span className="text-[9px] text-foreground-400">{item.timestamp}</span>
          <span className="text-[9px] font-bold text-emerald-600">+{item.points} pts</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Club Card ─── */
function ClubCompactCard({ club, pos }: { club: RfwClub; pos: number }) {
  const typeConfig = clubTypeColors[club.type];
  return (
    <Link
      to="/clubes"
      className="flex items-center gap-3 p-3 rounded-xl bg-background-50 hover:bg-background-100/70 transition-colors group cursor-pointer border border-background-100"
    >
      <div className="shrink-0">
        {pos === 1 ? <span className="text-base">🥇</span>
         : pos === 2 ? <span className="text-base">🥈</span>
         : pos === 3 ? <span className="text-base">🥉</span>
         : (
            <span className="w-6 h-6 rounded-full bg-background-100 flex items-center justify-center text-[11px] font-extrabold text-foreground-400">
              {pos}
            </span>
         )}
      </div>
      <img src={club.parentLogoUrl} alt={club.clubName} className="w-10 h-10 rounded-xl object-cover shrink-0 border border-background-200/70" loading="lazy" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-foreground-950 truncate group-hover:text-accent-600 transition-colors">{club.clubName}</p>
        <div className="flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-0.5 text-[9px] font-medium ${typeConfig.text}`}>
            <span className={`w-1 h-1 rounded-full ${typeConfig.dot}`}></span>
            {clubTypeLabels[club.type]}
          </span>
          <span className="text-[10px] text-foreground-400 truncate">· {club.parentName}</span>
        </div>
      </div>
      <div className="shrink-0 flex flex-col items-end gap-0.5">
        <span className="text-sm font-extrabold text-foreground-950">{formatNumber(club.clubScore)}</span>
        <span className={`text-[10px] font-semibold ${club.scoreChange > 0 ? 'text-emerald-600' : club.scoreChange < 0 ? 'text-red-500' : 'text-foreground-400'}`}>
          {club.scoreChange > 0 ? '+' : ''}{club.scoreChange}
        </span>
      </div>
    </Link>
  );
}

/* ─── Club Card with Join Button ─── */
function ClubFeatureCard({ club }: { club: RfwClub }) {
  const typeConfig = clubTypeColors[club.type];
  return (
    <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-background-300/60 transition-all duration-200 group">
      <div className="relative h-32 overflow-hidden">
        <img src={club.thumbnailUrl} alt={club.clubName} className="w-full h-full object-cover object-top" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/90 via-background-50/30 to-transparent"></div>
        <div className="absolute top-2.5 left-2.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border ${typeConfig.badge} ${typeConfig.text} backdrop-blur-sm`}>
            <span className={`w-1.5 h-1.5 rounded-full ${typeConfig.dot}`}></span>
            {clubTypeLabels[club.type]}
          </span>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <h4 className="text-sm font-extrabold text-foreground-950">{club.clubName}</h4>
          <p className="text-[10px] text-foreground-500">{club.parentName} {club.parentFrequency || ''}</p>
        </div>
      </div>
      <div className="p-4">
        <p className="text-[11px] text-foreground-500 leading-relaxed line-clamp-2 mb-3">{club.description}</p>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <span className="block text-sm font-extrabold text-foreground-950">{formatNumber(club.membersCount)}</span>
            <span className="text-[9px] text-foreground-400">Miembros</span>
          </div>
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <span className="block text-sm font-extrabold text-foreground-950">{club.engagementRate}%</span>
            <span className="text-[9px] text-foreground-400">Engagement</span>
          </div>
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <span className="block text-sm font-extrabold text-foreground-950">{club.activeChallengeCount}</span>
            <span className="text-[9px] text-foreground-400">Retos</span>
          </div>
        </div>
        <Link
          to="/clubes"
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          Unirme al Club
        </Link>
      </div>
    </div>
  );
}

type LandingTab = 'activos' | 'tendencia' | 'nuevos';

export default function ClubesSection() {
  const [activeTab, setActiveTab] = useState<LandingTab>('activos');

  const mostActive = useMemo(() => getMostActiveClubs(5), []);
  const trending = useMemo(() => getTrendingClubs(5), []);
  const newClubs = useMemo(() => getNewClubs(5), []);
  const top3 = useMemo(() => getTopClubs(3), []);

  const tabs: { id: LandingTab; label: string; icon: string }[] = [
    { id: 'activos', label: 'Clubes Más Activos', icon: 'ri-flashlight-line' },
    { id: 'tendencia', label: 'Clubes Tendencia', icon: 'ri-fire-line' },
    { id: 'nuevos', label: 'Clubes Nuevos', icon: 'ri-rocket-line' },
  ];

  const activeData = activeTab === 'activos' ? mostActive : activeTab === 'tendencia' ? trending : newClubs;

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></span>
            CLUBES DE FANS COMPLETOS
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Los Clubes son el <span className="text-accent-600">Corazón Social</span> de RFW
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Convertimos oyentes en comunidades activas. Publicaciones, retos, eventos, concursos, fotos, videos y rankings internos. Cada radio, programa y locutor tiene su Club Oficial.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Clubes', value: clubsSummary.totalClubs.toString(), icon: 'ri-group-line', color: 'text-accent-600 bg-accent-50' },
            { label: 'Miembros', value: formatNumber(clubsSummary.totalMembers), icon: 'ri-user-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Posts', value: formatNumber(clubsSummary.totalPosts), icon: 'ri-article-line', color: 'text-violet-600 bg-violet-50' },
            { label: 'Retos Activos', value: clubsSummary.totalActiveChallenges.toString(), icon: 'ri-sword-line', color: 'text-orange-600 bg-orange-50' },
            { label: 'Eventos', value: clubsSummary.totalEvents.toString(), icon: 'ri-calendar-event-line', color: 'text-rose-600 bg-rose-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-accent-500 text-background-50 shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Top 3 Podium */}
        <div className="flex items-end justify-center gap-3 md:gap-5 px-2 mb-8">
          {top3.map((club, i) => {
            const order = i === 0 ? [1, 0, 2] : [0, 1, 2];
            const displayClub = top3[order[i]];
            if (!displayClub) return null;
            return (
              <div key={displayClub.id} className="flex-1 max-w-[200px]">
                <ClubFeatureCard club={displayClub} />
              </div>
            );
          })}
        </div>

        {/* Featured grid + Activity sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Clubs list */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center">
                <i className={`${tabs.find(t => t.id === activeTab)?.icon} text-sm text-foreground-600`}></i>
              </div>
              <h3 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider">
                {tabs.find(t => t.id === activeTab)?.label}
              </h3>
            </div>
            <div className="space-y-2">
              {activeData.map((club, i) => (
                <ClubCompactCard key={club.id} club={club} pos={i + 1} />
              ))}
            </div>
          </div>

          {/* Right: Activity Feed */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center">
                <i className="ri-pulse-line text-sm text-foreground-600"></i>
              </div>
              <h3 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider">Actividad Reciente</h3>
            </div>
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {globalClubActivityFeed.slice(0, 8).map((item) => (
                <ActivityItem key={item.id} item={item} />
              ))}
            </div>
          </div>
        </div>

        {/* Club Sections */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-3">
          {[
            { label: 'Publicaciones', icon: 'ri-article-line', color: 'bg-primary-100 text-primary-600' },
            { label: 'Fotos', icon: 'ri-image-line', color: 'bg-rose-100 text-rose-600' },
            { label: 'Videos', icon: 'ri-vidicon-line', color: 'bg-violet-100 text-violet-600' },
            { label: 'Eventos', icon: 'ri-calendar-event-line', color: 'bg-amber-100 text-amber-600' },
            { label: 'Concursos', icon: 'ri-trophy-line', color: 'bg-orange-100 text-orange-600' },
            { label: 'Miembros', icon: 'ri-user-line', color: 'bg-emerald-100 text-emerald-600' },
            { label: 'Ranking', icon: 'ri-bar-chart-2-line', color: 'bg-sky-100 text-sky-600' },
          ].map((s) => (
            <div key={s.label} className="text-center p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 cursor-pointer">
              <div className={`w-9 h-9 rounded-full ${s.color} flex items-center justify-center mx-auto mb-1.5`}>
                <i className={`${s.icon} text-sm`}></i>
              </div>
              <span className="text-[10px] font-semibold text-foreground-700">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Score Actions */}
        <div className="mt-10 p-5 rounded-2xl bg-background-50 border border-background-200/70">
          <h4 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
            <i className="ri-flashlight-line text-accent-500"></i>
            Club Score — ¿Cómo sumás puntos en tu Club?
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {clubScoreActions.map((action) => (
              <div key={action.key} className={`flex items-center gap-2.5 p-3 rounded-xl ${action.bg}`}>
                <i className={`${action.icon} ${action.color} text-sm`}></i>
                <div>
                  <p className="text-[10px] font-bold text-foreground-950">+{action.points} pts</p>
                  <p className="text-[9px] text-foreground-500">{action.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/clubes"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-group-line"></i>
            Explorar Todos los Clubes
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-accent-100/70 border border-accent-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-radio-line text-lg text-background-50"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Cada Radio debe poder tener su Club Oficial.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Los Clubes son el motor social de RFW. No importa si tu radio es grande o pequeña, comunitaria o comercial — cada frecuencia en RFW puede y debe tener su espacio de comunidad. Publicaciones, fotos, videos, eventos, concursos, rankings, moderadores: todo lo que una comunidad activa necesita para crecer.
          </p>
        </div>
      </div>
    </section>
  );
}